import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { addonsService, imageService } from '../services/api';
import PriceBreakdownButton from './PriceBreakdownButton';
import { useIsMobile } from '../hooks';
import { RefreshCw as RefreshCwIcon } from 'lucide-react';

// Colors and Icons (matching existing style)
const Colors = {
  primary: '#1A3E2A',
  secondary: '#5F6B61',
  accent: '#1A3E2A',
  warning: '#B68235',
  danger: '#A13F2F',
  success: '#163626',
  white: '#ffffff',
  light: '#F5F3EB',
  border: '#E3DFD3',
  muted: '#A5A89B',
  available: '#1A3E2A',
  occupied: '#A13F2F'
};

const Icons = {
  add: '',
  edit: '',
  delete: '',
  image: '',
  addon: '',
  close: '✕',
  upload: '',
  save: ''
};

function AddOnManagement({ user, onDataUpdate }) {
  const isMobile = useIsMobile();
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [addonImages, setAddonImages] = useState({});
  
  // Form states
  const [addonForm, setAddonForm] = useState({
    name: '',
    description: '',
    price: '',
    sgstRate: '',
    cgstRate: '',
    igstRate: ''
  });
  
  // Image upload states
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [imageDescription, setImageDescription] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImages, setUploadingImages] = useState(false);

  useEffect(() => {
    loadAddons();
  }, []);

  const loadAddons = async () => {
    try {
      setLoading(true);
      const response = await addonsService.getAll({ limit: 100 });
      const addonsData = response.data?.addOns || response.addOns || [];
      setAddons(addonsData);
      
      // Load images for all addons
      await loadAllAddonImages(addonsData);
    } catch (error) {
      toast.error('Failed to load add-on services');
      console.error('Error loading addons:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllAddonImages = async (addonsData) => {
    try {
      const imagePromises = addonsData.map(async (addon) => {
        try {
          const imagesResponse = await imageService.getImages('addon', addon.id);
          return { addonId: addon.id, images: imagesResponse.data || [] };
        } catch (error) {
          return { addonId: addon.id, images: [] };
        }
      });

      const imageResults = await Promise.all(imagePromises);
      const imageMap = {};
      imageResults.forEach(({ addonId, images }) => {
        imageMap[addonId] = images;
      });
      setAddonImages(imageMap);
    } catch (error) {
      console.error('Error loading addon images:', error);
    }
  };

  const loadAddonImages = async (addonId) => {
    try {
      const imagesResponse = await imageService.getImages('addon', addonId);
      setAddonImages(prev => ({
        ...prev,
        [addonId]: imagesResponse.data || []
      }));
    } catch (error) {
      console.error('Error loading addon images:', error);
    }
  };

  const resetForm = () => {
    setAddonForm({
      name: '',
      description: '',
      price: '',
      sgstRate: '',
      cgstRate: '',
      igstRate: ''
    });
  };

  const handleCreateAddon = async (e) => {
    e.preventDefault();
    if (!addonForm.name.trim()) {
      toast.error('Please enter an add-on service name');
      return;
    }

    // Validate price if provided
    let price = null;
    if (addonForm.price && addonForm.price.trim()) {
      price = parseFloat(addonForm.price);
      if (isNaN(price) || price < 0) {
        toast.error('Please enter a valid price or leave it empty for custom pricing');
        return;
      }
    }

    // Validate GST rates
    const sgstRate = addonForm.sgstRate ? parseFloat(addonForm.sgstRate) : 0;
    const cgstRate = addonForm.cgstRate ? parseFloat(addonForm.cgstRate) : 0;
    const igstRate = addonForm.igstRate ? parseFloat(addonForm.igstRate) : 0;

    if (sgstRate < 0 || sgstRate > 100 || cgstRate < 0 || cgstRate > 100 || igstRate < 0 || igstRate > 100) {
      toast.error('GST rates must be between 0 and 100');
      return;
    }

    try {
      const addonData = {
        name: addonForm.name.trim(),
        description: addonForm.description.trim(),
        sgstRate: sgstRate,
        cgstRate: cgstRate,
        igstRate: igstRate
      };
      
      // Only include price if it was provided
      if (price !== null) {
        addonData.price = price;
      }

      await addonsService.create(addonData);
      toast.success('Add-on service created successfully');
      setShowAddModal(false);
      resetForm();
      loadAddons();
      onDataUpdate?.();
    } catch (error) {
      toast.error('Failed to create add-on service');
      console.error('Error creating addon:', error);
    }
  };

  const handleEditAddon = async (e) => {
    e.preventDefault();
    if (!addonForm.name.trim() || !selectedAddon) {
      toast.error('Please enter an add-on service name');
      return;
    }

    // Validate price if provided
    let price = null;
    if (addonForm.price && addonForm.price.trim()) {
      price = parseFloat(addonForm.price);
      if (isNaN(price) || price < 0) {
        toast.error('Please enter a valid price or leave it empty for custom pricing');
        return;
      }
    }

    // Validate GST rates
    const sgstRate = addonForm.sgstRate ? parseFloat(addonForm.sgstRate) : 0;
    const cgstRate = addonForm.cgstRate ? parseFloat(addonForm.cgstRate) : 0;
    const igstRate = addonForm.igstRate ? parseFloat(addonForm.igstRate) : 0;

    if (sgstRate < 0 || sgstRate > 100 || cgstRate < 0 || cgstRate > 100 || igstRate < 0 || igstRate > 100) {
      toast.error('GST rates must be between 0 and 100');
      return;
    }

    try {
      const addonData = {
        name: addonForm.name.trim(),
        description: addonForm.description.trim(),
        sgstRate: sgstRate,
        cgstRate: cgstRate,
        igstRate: igstRate
      };
      
      // Include price (null or value)
      addonData.price = price;

      await addonsService.update(selectedAddon.id, addonData);
      toast.success('Add-on service updated successfully');
      setShowEditModal(false);
      setSelectedAddon(null);
      resetForm();
      loadAddons();
      onDataUpdate?.();
    } catch (error) {
      toast.error('Failed to update add-on service');
      console.error('Error updating addon:', error);
    }
  };

  const handleDeleteAddon = async (addon) => {
    if (!window.confirm(`Are you sure you want to delete "${addon.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await addonsService.delete(addon.id);
      toast.success('Add-on service deleted successfully');
      loadAddons();
      onDataUpdate?.();
    } catch (error) {
      toast.error('Failed to delete add-on service: ' + (error.message || 'Unknown error'));
      console.error('Error deleting addon:', error);
    }
  };

  const openEditModal = (addon) => {
    setSelectedAddon(addon);
    setAddonForm({
      name: addon.name,
      description: addon.description || '',
      price: addon.price ? addon.price.toString() : '',
      sgstRate: addon.sgstRate ? addon.sgstRate.toString() : '',
      cgstRate: addon.cgstRate ? addon.cgstRate.toString() : '',
      igstRate: addon.igstRate ? addon.igstRate.toString() : ''
    });
    setShowEditModal(true);
  };

  // Image Management Functions
  const openImageModal = (addon) => {
    setSelectedAddon(addon);
    setSelectedFiles([]);
    setImageDescription('');
    setImagePreview(null);
    setShowImageModal(true);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    
    // Create preview for single image
    if (files.length === 1) {
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(files[0]);
    } else {
      setImagePreview(null);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedAddon || selectedFiles.length === 0) {
      toast.error('Please select an add-on service and at least one image');
      return;
    }

    setUploadingImages(true);
    try {
      const formData = new FormData();
      
      if (selectedFiles.length === 1) {
        // Single image upload
        formData.append('image', selectedFiles[0]);
      } else {
        // Multiple image upload
        selectedFiles.forEach(file => {
          formData.append('images', file);
        });
      }
      
      formData.append('addOnServiceId', selectedAddon.id);
      if (imageDescription.trim()) {
        formData.append('description', imageDescription.trim());
      }

      if (selectedFiles.length === 1) {
        await imageService.uploadSingle(formData);
        toast.success('Image uploaded successfully');
      } else {
        await imageService.uploadMultiple(formData);
        toast.success(`${selectedFiles.length} images uploaded successfully`);
      }

      // Reload images for this addon
      await loadAddonImages(selectedAddon.id);
      
      // Reset form
      setSelectedFiles([]);
      setImageDescription('');
      setImagePreview(null);
      setShowImageModal(false);
      
    } catch (error) {
      toast.error('Failed to upload images: ' + (error.message || 'Unknown error'));
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (imageId, addonId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      await imageService.deleteImage(imageId);
      toast.success('Image deleted successfully');
      await loadAddonImages(addonId);
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleUpdateImageDescription = async (imageId, newDescription, addonId) => {
    try {
      await imageService.updateImage(imageId, newDescription);
      toast.success('Image description updated');
      await loadAddonImages(addonId);
    } catch (error) {
      toast.error('Failed to update image description');
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <div style={{ fontSize: '48px', marginBottom: '20px' }}><RefreshCwIcon size="1em" aria-hidden="true" /></div>
        <div>Loading add-on services...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: isMobile ? '12px' : '20px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'stretch' : 'center',
        flexDirection: isMobile ? 'column' : 'row',
        gap: '12px',
        marginBottom: '30px'
      }}>
        <div>
          <h1 style={{
            margin: 0,
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: '800',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <span style={{ fontSize: '32px' }}>{Icons.addon}</span>
            Add-ons Management
          </h1>
          <p style={{
            margin: isMobile ? '4px 0 0 0' : '4px 0 0 44px',
            color: Colors.muted,
            fontSize: '14px'
          }}>
            Manage additional services, pricing, and images
          </p>
        </div>
        
        {(['STAFF', 'PROPERTYADMIN', 'SUPERADMIN'].includes(user?.role)) && (
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease-in-out',
              backgroundColor: Colors.accent,
              color: Colors.white,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
              width: isMobile ? '100%' : 'auto',
              justifyContent: 'center'
            }}
          >
            <span>{Icons.add}</span>
            Add New Service
          </button>
        )}
      </div>

      {/* Add-ons Grid */}
      {addons.length === 0 ? (
        <div style={{
          backgroundColor: Colors.white,
          border: `1px solid ${Colors.border}`,
          borderRadius: '12px',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          padding: '80px 40px',
        }}>
          <div style={{
            fontSize: '64px',
            color: Colors.muted,
            marginBottom: '24px'
          }}>
            {Icons.addon}
          </div>
          <h3 style={{
            margin: '0 0 12px 0',
            color: Colors.secondary,
            fontSize: '20px',
            fontWeight: '600'
          }}>
            No add-on services configured
          </h3>
          <p style={{
            color: Colors.muted,
            marginBottom: '24px',
            fontSize: '14px'
          }}>
            Create your first add-on service to enhance guest experiences
          </p>
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              backgroundColor: Colors.accent,
              color: Colors.white
            }}
          >
            <span>{Icons.add}</span>
            Add First Service
          </button>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(400px, 1fr))',
          gap: '24px'
        }}>
          {addons.map(addon => (
            <div
              key={addon.id}
              style={{
                backgroundColor: Colors.white,
                border: `1px solid ${Colors.border}`,
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
                transition: 'all 0.2s ease-in-out'
              }}
            >
              {/* Service Header */}
              <div style={{ padding: '24px 24px 16px 24px' }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '10px',
                  marginBottom: '12px'
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '20px',
                    fontWeight: '700',
                    color: Colors.secondary
                  }}>
                    {addon.name}
                  </h3>
                  <div style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: addon.price ? Colors.accent : Colors.muted,
                    padding: '4px 12px',
                    backgroundColor: addon.price ? `${Colors.accent}15` : `${Colors.muted}15`,
                    borderRadius: '6px'
                  }}>
                    {addon.price ? `₹${addon.price.toFixed(2)}` : 'Custom Pricing'}
                  </div>
                </div>
                
                {addon.description && (
                  <p style={{
                    margin: '0 0 12px 0',
                    color: Colors.muted,
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {addon.description}
                  </p>
                )}

                {/* GST Information */}
                {(addon.sgstRate || addon.cgstRate || addon.igstRate) && (
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap',
                    marginTop: '8px'
                  }}>
                    {addon.sgstRate > 0 && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: `${Colors.accent}15`,
                        color: Colors.accent,
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px'
                      }}>
                        SGST: {addon.sgstRate}%
                      </span>
                    )}
                    {addon.cgstRate > 0 && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: `${Colors.accent}15`,
                        color: Colors.accent,
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px'
                      }}>
                        CGST: {addon.cgstRate}%
                      </span>
                    )}
                    {addon.igstRate > 0 && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: `${Colors.accent}15`,
                        color: Colors.accent,
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '4px'
                      }}>
                        IGST: {addon.igstRate}%
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Service Images */}
              <div style={{ padding: '0 24px 16px 24px' }}>
                {addonImages[addon.id] && addonImages[addon.id].length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: addonImages[addon.id].length === 1 ? '1fr' : 'repeat(auto-fit, minmax(80px, 1fr))',
                    gap: '8px',
                    marginBottom: '16px'
                  }}>
                    {addonImages[addon.id].slice(0, 4).map((image, index) => (
                      <div
                        key={image.id}
                        style={{
                          position: 'relative',
                          aspectRatio: '1',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: Colors.light
                        }}
                      >
                        <img
                          src={image.url}
                          alt={image.description || addon.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        {addonImages[addon.id].length > 4 && index === 3 && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: Colors.white,
                            fontSize: '14px',
                            fontWeight: '600'
                          }}>
                            +{addonImages[addon.id].length - 4} more
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '24px',
                    color: Colors.muted,
                    fontSize: '13px',
                    backgroundColor: Colors.light,
                    borderRadius: '8px',
                    border: `1px dashed ${Colors.border}`,
                    marginBottom: '16px'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>{Icons.image}</div>
                    No images uploaded yet
                  </div>
                )}
              </div>

              {/* Service Actions */}
              <div style={{
                padding: '16px 24px',
                backgroundColor: Colors.light,
                borderTop: `1px solid ${Colors.border}`,
                display: 'flex',
                gap: '8px',
                justifyContent: 'flex-end',
                flexWrap: 'wrap'
              }}>
                <PriceBreakdownButton
                  entityType="addOnService"
                  entityId={addon.id}
                  entity={{ name: addon.name, basePrice: addon.price }}
                  onUpdate={loadAddons}
                  variant="secondary"
                  size="small"
                />
                
                <button
                  onClick={() => openImageModal(addon)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    backgroundColor: Colors.primary,
                    color: Colors.white
                  }}
                >
                  <span>{Icons.image}</span>
                  Images
                </button>
                
                {(['STAFF', 'PROPERTYADMIN', 'SUPERADMIN'].includes(user?.role)) && (
                  <>
                    <button
                      onClick={() => openEditModal(addon)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '8px 12px',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        backgroundColor: Colors.warning,
                        color: Colors.white
                      }}
                    >
                      <span>{Icons.edit}</span>
                      Edit
                    </button>
                    
                    {(['PROPERTYADMIN', 'SUPERADMIN'].includes(user?.role)) && (
                      <button
                        onClick={() => handleDeleteAddon(addon)}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '8px 12px',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          backgroundColor: Colors.danger,
                          color: Colors.white
                        }}
                      >
                        <span>{Icons.delete}</span>
                        Delete
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Service Modal */}
      {showAddModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: Colors.white,
            borderRadius: '12px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ padding: '24px' }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '24px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{Icons.add}</span>
                Add New Service
              </h3>
              
              <form onSubmit={handleCreateAddon}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: Colors.secondary
                  }}>
                    Service Name *
                  </label>
                  <input
                    type="text"
                    value={addonForm.name}
                    onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
                    placeholder="e.g., Spa Treatment, Private Yoga Session"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${Colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: Colors.secondary
                  }}>
                    Description
                  </label>
                  <textarea
                    value={addonForm.description}
                    onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
                    placeholder="Describe the service and what's included..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${Colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: Colors.secondary
                  }}>
                    Price (₹) *
                  </label>
                  <input
                    type="number"
                    value={addonForm.price}
                    onChange={(e) => setAddonForm({ ...addonForm, price: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${Colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                </div>

                {/* GST Fields */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{
                    margin: '0 0 12px 0',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: Colors.secondary
                  }}>
                    GST Configuration
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontWeight: '500',
                        fontSize: '14px',
                        color: Colors.secondary
                      }}>
                        SGST Rate (%)
                      </label>
                      <input
                        type="number"
                        value={addonForm.sgstRate}
                        onChange={(e) => setAddonForm({ ...addonForm, sgstRate: e.target.value })}
                        placeholder="9.0"
                        min="0"
                        max="100"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: `1px solid ${Colors.border}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontWeight: '500',
                        fontSize: '14px',
                        color: Colors.secondary
                      }}>
                        CGST Rate (%)
                      </label>
                      <input
                        type="number"
                        value={addonForm.cgstRate}
                        onChange={(e) => setAddonForm({ ...addonForm, cgstRate: e.target.value })}
                        placeholder="9.0"
                        min="0"
                        max="100"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: `1px solid ${Colors.border}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontWeight: '500',
                        fontSize: '14px',
                        color: Colors.secondary
                      }}>
                        IGST Rate (%)
                      </label>
                      <input
                        type="number"
                        value={addonForm.igstRate}
                        onChange={(e) => setAddonForm({ ...addonForm, igstRate: e.target.value })}
                        placeholder="18.0"
                        min="0"
                        max="100"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: `1px solid ${Colors.border}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: Colors.muted,
                    fontStyle: 'italic'
                  }}>
                    * SGST + CGST for intra-state transactions, IGST for inter-state transactions
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      resetForm();
                    }}
                    style={{
                      padding: '12px 20px',
                      border: `1px solid ${Colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      backgroundColor: Colors.white,
                      color: Colors.secondary
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '12px 20px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      backgroundColor: Colors.accent,
                      color: Colors.white,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>{Icons.save}</span>
                    Create Service
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Service Modal */}
      {showEditModal && selectedAddon && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: Colors.white,
            borderRadius: '12px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ padding: '24px' }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '24px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{Icons.edit}</span>
                Edit Service
              </h3>
              
              <form onSubmit={handleEditAddon}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: Colors.secondary
                  }}>
                    Service Name *
                  </label>
                  <input
                    type="text"
                    value={addonForm.name}
                    onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
                    placeholder="e.g., Spa Treatment, Private Yoga Session"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${Colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: Colors.secondary
                  }}>
                    Description
                  </label>
                  <textarea
                    value={addonForm.description}
                    onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
                    placeholder="Describe the service and what's included..."
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${Colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: Colors.secondary
                  }}>
                    Price (₹) *
                  </label>
                  <input
                    type="number"
                    value={addonForm.price}
                    onChange={(e) => setAddonForm({ ...addonForm, price: e.target.value })}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${Colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    required
                  />
                </div>

                {/* GST Fields */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{
                    margin: '0 0 12px 0',
                    fontSize: '16px',
                    fontWeight: '600',
                    color: Colors.secondary
                  }}>
                    GST Configuration
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap: '12px' }}>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontWeight: '500',
                        fontSize: '14px',
                        color: Colors.secondary
                      }}>
                        SGST Rate (%)
                      </label>
                      <input
                        type="number"
                        value={addonForm.sgstRate}
                        onChange={(e) => setAddonForm({ ...addonForm, sgstRate: e.target.value })}
                        placeholder="9.0"
                        min="0"
                        max="100"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: `1px solid ${Colors.border}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontWeight: '500',
                        fontSize: '14px',
                        color: Colors.secondary
                      }}>
                        CGST Rate (%)
                      </label>
                      <input
                        type="number"
                        value={addonForm.cgstRate}
                        onChange={(e) => setAddonForm({ ...addonForm, cgstRate: e.target.value })}
                        placeholder="9.0"
                        min="0"
                        max="100"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: `1px solid ${Colors.border}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '6px',
                        fontWeight: '500',
                        fontSize: '14px',
                        color: Colors.secondary
                      }}>
                        IGST Rate (%)
                      </label>
                      <input
                        type="number"
                        value={addonForm.igstRate}
                        onChange={(e) => setAddonForm({ ...addonForm, igstRate: e.target.value })}
                        placeholder="18.0"
                        min="0"
                        max="100"
                        step="0.1"
                        style={{
                          width: '100%',
                          padding: '10px',
                          border: `1px solid ${Colors.border}`,
                          borderRadius: '6px',
                          fontSize: '14px',
                          boxSizing: 'border-box'
                        }}
                      />
                    </div>
                  </div>
                  <div style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: Colors.muted,
                    fontStyle: 'italic'
                  }}>
                    * SGST + CGST for intra-state transactions, IGST for inter-state transactions
                  </div>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedAddon(null);
                      resetForm();
                    }}
                    style={{
                      padding: '12px 20px',
                      border: `1px solid ${Colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      backgroundColor: Colors.white,
                      color: Colors.secondary
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '12px 20px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      backgroundColor: Colors.warning,
                      color: Colors.white,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>{Icons.save}</span>
                    Update Service
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Image Management Modal */}
      {showImageModal && selectedAddon && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: Colors.white,
            borderRadius: '12px',
            width: isMobile ? '100%' : '700px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ padding: '24px' }}>
              <h3 style={{
                margin: '0 0 20px 0',
                fontSize: '24px',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span>{Icons.image}</span>
                Manage Images - {selectedAddon.name}
              </h3>

              {/* Current Images */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: Colors.secondary
                }}>
                  Current Images ({addonImages[selectedAddon.id]?.length || 0})
                </h4>
                
                {addonImages[selectedAddon.id] && addonImages[selectedAddon.id].length > 0 ? (
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(120px, 1fr))',
                    gap: '12px'
                  }}>
                    {addonImages[selectedAddon.id].map((image) => (
                      <div
                        key={image.id}
                        style={{
                          position: 'relative',
                          aspectRatio: '1',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: Colors.light,
                          border: `1px solid ${Colors.border}`
                        }}
                      >
                        <img
                          src={image.url}
                          alt={image.description || selectedAddon.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                        <button
                          onClick={() => handleDeleteImage(image.id, selectedAddon.id)}
                          style={{
                            position: 'absolute',
                            top: '4px',
                            right: '4px',
                            backgroundColor: 'rgba(161, 63, 47, 0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 6px',
                            cursor: 'pointer',
                            color: Colors.white,
                            fontSize: '12px'
                          }}
                        >
                          {Icons.close}
                        </button>
                        {image.description && (
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            color: Colors.white,
                            padding: '4px 6px',
                            fontSize: '11px',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap'
                          }}>
                            {image.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '24px',
                    color: Colors.muted,
                    fontSize: '14px',
                    backgroundColor: Colors.light,
                    borderRadius: '8px',
                    border: `1px dashed ${Colors.border}`
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '8px' }}>{Icons.image}</div>
                    No images uploaded yet
                  </div>
                )}
              </div>

              {/* Upload New Images */}
              <div style={{
                border: `2px dashed ${Colors.border}`,
                borderRadius: '8px',
                padding: '24px',
                textAlign: 'center',
                marginBottom: '20px'
              }}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    backgroundColor: Colors.primary,
                    color: Colors.white,
                    marginBottom: '12px'
                  }}
                >
                  <span>{Icons.upload}</span>
                  Select Images
                </label>
                
                <p style={{
                  margin: '8px 0 0 0',
                  color: Colors.muted,
                  fontSize: '12px'
                }}>
                  Select one or multiple images (JPEG, PNG, WebP)
                </p>

                {selectedFiles.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <p style={{
                      margin: '0 0 8px 0',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: Colors.secondary
                    }}>
                      Selected Files: {selectedFiles.length}
                    </p>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      justifyContent: 'center'
                    }}>
                      {Array.from(selectedFiles).map((file, index) => (
                        <span
                          key={index}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: Colors.light,
                            borderRadius: '4px',
                            fontSize: '12px',
                            color: Colors.secondary
                          }}
                        >
                          {file.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {imagePreview && (
                  <div style={{
                    marginTop: '16px',
                    position: 'relative',
                    display: 'inline-block'
                  }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: '200px',
                        maxHeight: '200px',
                        borderRadius: '8px',
                        border: `1px solid ${Colors.border}`
                      }}
                    />
                    <button
                      onClick={() => {
                        setSelectedFiles([]);
                        setImagePreview(null);
                      }}
                      style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        backgroundColor: 'rgba(161, 63, 47, 0.9)',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        cursor: 'pointer',
                        color: Colors.white,
                        fontSize: '12px'
                      }}
                    >
                      {Icons.close}
                    </button>
                  </div>
                )}
              </div>

              {/* Image Description */}
              {selectedFiles.length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{
                    display: 'block',
                    marginBottom: '8px',
                    fontWeight: '600',
                    color: Colors.secondary
                  }}>
                    Image Description (Optional)
                  </label>
                  <input
                    type="text"
                    value={imageDescription}
                    onChange={(e) => setImageDescription(e.target.value)}
                    placeholder="Describe the images..."
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${Colors.border}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              )}

              {/* Modal Actions */}
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <button
                  onClick={() => {
                    setShowImageModal(false);
                    setSelectedAddon(null);
                    setSelectedFiles([]);
                    setImageDescription('');
                    setImagePreview(null);
                  }}
                  style={{
                    padding: '12px 20px',
                    border: `1px solid ${Colors.border}`,
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    backgroundColor: Colors.white,
                    color: Colors.secondary
                  }}
                >
                  Close
                </button>
                
                {selectedFiles.length > 0 && (
                  <button
                    onClick={handleImageUpload}
                    disabled={uploadingImages}
                    style={{
                      padding: '12px 20px',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: uploadingImages ? 'not-allowed' : 'pointer',
                      backgroundColor: uploadingImages ? Colors.muted : Colors.accent,
                      color: Colors.white,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>{uploadingImages ? '' : Icons.upload}</span>
                    {uploadingImages ? 'Uploading...' : `Upload ${selectedFiles.length} Image${selectedFiles.length > 1 ? 's' : ''}`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AddOnManagement;